import { Injectable } from '@angular/core';
import { getCtx, withCtx } from '../../core/context';
import { 
  collection, 
  doc, 
  deleteDoc, 
  getDoc, 
  getDocs, 
  getFirestore, 
  PartialWithFieldValue, 
  runTransaction, 
  setDoc, 
  SetOptions, 
  Transaction as FirestoreTransaction,
  collectionGroup,
  query,
  limit,
  startAt,
  startAfter,
  where,
  limitToLast,
  orderBy,
  arrayUnion,
  onSnapshot as onValue
 } from 'firebase/firestore';
import { DocumentData, DocumentSnapshot, arrayRemove, onSnapshot } from '@angular/fire/firestore';
import { getDatabase, ref, get } from "firebase/database";


export type DBOnSnapShotFnType = (snap: DocumentSnapshot<DocumentData>) => void;


type ArrayKeys<T> = {
  [K in keyof T]: T[K] extends (infer I)[] ? K : never
}[keyof T];

type ElementType<T, K extends keyof T> = T[K] extends (infer I)[] ? I : never;

export type DBQueryWhereIn<T> = {
  column: ArrayKeys<T>;
  value: string | boolean | number | null;
  // value: ElementType<T, ArrayKeys<T>>;
};

export const DBArrayUnion = (value: any) => arrayUnion(value);
export const DBArrayRemove = (value: any) => arrayRemove(value);
export type WhereParams = {
  column: string;
  value: string | number;
};
@Injectable({
  providedIn: 'root'
})
export class DatabaseService {

  static instance: DatabaseService;

  public pendingSync: boolean;

  public get isLoading(): boolean {
    return this.loading > 0;
  }
  private _loading: number = 0;

  private get loading() {
    return this._loading;
  }

  private set loading(value) {
    this._loading = Math.max(value, 0);
  }

  public startLoading() {
    this.loading++;
  }
  public stopLoading() {
    this.loading--;
  }

  constructor() { }

  public async init() {
    DatabaseService.instance = this;
  }

  public async withTx<T>(
    block: (transaction: Transaction) => Promise<T>
  ) {
    return withCtx<Promise<T>, TransactionCtx>({}, async (ctx) => {
      return await runTransaction(getFirestore(), async (transaction) => {
        this.startLoading();
        ctx.transaction = transaction;
        try {
          return await block(transaction)
        } catch (e) {
          console.log("Transaction error:", e)
          throw e;
        } finally {
          delete ctx.transaction;
          this.stopLoading();
        }
        // Promise.resolve();       
      })
    });
  }

  public async set<T>(
    reference: string, 
    data: PartialWithFieldValue<T>, 
    options: SetOptions & { onSnapshot?: DBOnSnapShotFnType } = { merge: true }) {
    // console.log("database set", reference, data)

    const normalize = (data: any) => {
      for (const k of Object.keys(data)) {
        if (data[k] == undefined) {
          delete data[k];
        }
      }
    };

    if (data instanceof Array) {
      for (let d of data) {
        normalize(d);
      }
    } else {
      normalize(data);
    }

    this.startLoading()
    try {
      const regex = new RegExp(`(/)`, 'g');
      const matches = reference.match(regex);
      const count = matches ? matches.length : 0;
      const ctx = getCtx<TransactionCtx>();

      if (count % 2 != 0) {
        const ref = doc(getFirestore(), reference);

        if (options.onSnapshot) {
          onSnapshot(ref, (snap) => options.onSnapshot(snap));
        }
        if (ctx?.transaction) {
          return ctx.transaction.set(ref, data as any, options);
        } else {
          return setDoc(ref, data as any, options);
        }
      } else {

        if (data instanceof Array) {

          const save = async (transaction: FirestoreTransaction) => {
            for (let d of data) {
              const ref = doc(getFirestore(), `${reference}/${d.id}`);
              transaction.set(ref, d.export ? d.export.apply() : d, options)
            }
            return transaction;
          }
          if (ctx?.transaction) {
            return save(ctx.transaction);
          } else {
            return this.withTx(async (transaction) => save(transaction));
          } 
        } else {
          throw new Error("db data error")
        }

      }
    } catch(e) { 
      throw e;
    } finally {
      this.stopLoading()
    }
  }

  public async get<T>(reference: string, constraints: { 
    limit?: number; 
    startAfter?: string | number; 
    whereEQ?: WhereParams | WhereParams[];
    whereIN?: DBQueryWhereIn<T>;
    whereIncludes?: WhereParams;
    whereGT?: {
      includeEqual?: boolean;
    } & WhereParams;
    whereLT?: {
      includeEqual?: boolean;
    } & WhereParams;
    orderBy?: {
      column: string;
      sort: 'asc' | 'desc'
    }} = {}) {
    this.startLoading()
    try {
      
      const regex = new RegExp(`(/)`, 'g');
      const matches = reference.match(regex);
      const count = matches ? matches.length : 0;
      const _reference = reference.split("/");

      if (count % 2 != 0) {
        const ref = doc(getFirestore(), reference);
        
        const docSnap = await getDoc(ref);

        if (docSnap.exists()) {
          return docSnap.data() as T;
        } else {
          return null;
        }
      } else {
        const subcollection = _reference.splice(-1,1)[0];

        const sub = collection(doc(getFirestore(), _reference.join("/")), subcollection);

        const _constraints = [orderBy(constraints.orderBy?.column || 'id',constraints.orderBy?.sort || 'asc'),limit(constraints.limit || 15)];

        if (constraints.startAfter) {
          _constraints.push(startAfter(constraints.startAfter) as any);
        }

        if (constraints.whereGT) {
          _constraints.push(where(constraints.whereGT.column, constraints.whereGT.includeEqual ? '>=' : '>', constraints.whereGT.value) as any);
        }

        if (constraints.whereLT) {
          _constraints.push(where(constraints.whereLT.column, constraints.whereLT.includeEqual ? '<=' : '<', constraints.whereLT.value) as any);
        }

        if (constraints.whereEQ) {
          if (Array.isArray(constraints.whereEQ)) {
            for (const whereEQ of constraints.whereEQ) {
              _constraints.push(where(whereEQ.column, '==', whereEQ.value) as any);
            }
          } else {
            _constraints.push(where(constraints.whereEQ.column, '==', constraints.whereEQ.value) as any);
          }
        }

        if (constraints.whereIN) {
          _constraints.push(where(String(constraints.whereIN.column), 'in', constraints.whereIN.value) as any);
        }

        if (constraints.whereIncludes) {
          _constraints.push(where(constraints.whereIncludes.column.toString(), 'array-contains', constraints.whereIncludes.value) as any);
        }

        const q = query(sub, ..._constraints);
        
        const docsSnap = await getDocs(q);
        const data = [];
        docsSnap.forEach(docSnap => {
          data.push(docSnap.data() as T);
        });
        return data as T[];
      }
    } finally {
      this.stopLoading()
    }
  }

  public async delete(reference: string) {
    console.log("database delete", reference)
    this.startLoading()
    try {
      const ctx = getCtx<TransactionCtx>();
      const regex = new RegExp(`(/)`, 'g');
      const matches = reference.match(regex);
      const count = matches ? matches.length : 0;
      if (count % 2 != 0) {
        const ref = doc(getFirestore(), reference);
        
        if (ctx?.transaction) {
          ctx.transaction.delete(ref);
        } else {
          await deleteDoc(ref);
        }
        return true;
      } else {
        console.log("Can't delete a full subcollection")
        return false;
      }
    } catch(e) {
      console.log(e);
      return false;
    } finally {
      this.stopLoading()
    }
  }
  
  public async getCache<T>(reference: string): Promise<T> {
      const db = getDatabase();
      const cacheRef = ref(db, reference);
      const snapshot = await get(cacheRef);
      

      return snapshot.val();

  }

  public async listenCache<T>(reference: string, callback: (data: T) => void) {
    const db = getDatabase();
    const cacheRef = ref(db, reference);
    
    // onValue(cacheRef, (snapshot) => {
    //   const data = snapshot.val();
    //   callback(data);
    // });

  }
}

export type Transaction = FirestoreTransaction;
export type TransactionCtx = {
  transaction: Transaction;
}
